import { datadogRum } from "@datadog/browser-rum";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useMutation } from "react-query";

import { completeSession as postCompleteSession } from "api/completeSession";
import { selectedDamagedPartsAtom } from "atoms/damageReview";
import { serverSessionAtom } from "atoms/serverSession";
import { sessionIdAtom } from "atoms/sessionId";

import { clientIdAtom } from "../../atoms/clientId";
import { completeSessionErrorAtom, completeSessionIsLoadingAtom, completeSessionIsSuccessAtom } from "./atoms";

type UseCompleteOptions = {
  onSuccess?: () => void;
};

export const useCompleteSession = ({ onSuccess: onSuccessCallback }: UseCompleteOptions = {}) => {
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const damagedParts = useAtomValue(selectedDamagedPartsAtom);
  const serverSession = useAtomValue(serverSessionAtom);
  const setServerSession = useSetAtom(serverSessionAtom);

  const [completeSessionLoading, setCompleteSessionLoading] = useAtom(completeSessionIsLoadingAtom);
  const [completeSessionError, setCompleteSessionError] = useAtom(completeSessionErrorAtom);
  const [sessionCompleted, setSessionCompleted] = useAtom(completeSessionIsSuccessAtom);

  const {
    isLoading,
    mutate: onCompleteSession,
    error,
    isSuccess,
  } = useMutation("complete", () => postCompleteSession(sessionId, clientId, { damagedParts }), {
    onSuccess: (response) => {
      // Update serve session as it is fetched only on page loading
      setServerSession(response.data);
      if (onSuccessCallback) onSuccessCallback();
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Failed to complete session",
      });
    },
  });

  const completeSession = useCallback(() => {
    // Complete session only is it hasn't been completed yet
    if (!serverSession?.completedAt) {
      onCompleteSession();
    }
  }, [onCompleteSession, serverSession?.completedAt]);

  useEffect(() => {
    setCompleteSessionLoading(isLoading);
  }, [isLoading, setCompleteSessionLoading]);

  useEffect(() => {
    setCompleteSessionError(error);
  }, [error, setCompleteSessionError]);

  useEffect(() => {
    setSessionCompleted(isSuccess);
  }, [isSuccess, setSessionCompleted]);

  return {
    completeSession,
    completeSessionLoading,
    sessionCompleted,
    completeSessionError,
  };
};
